import * as THREE from 'three'
import Experience from "../Experience.js"

export default class Butterfly {
    constructor() {
        this.experience = new Experience()
        this.resources = this.experience.resources
        this.time = this.experience.time
        this.debug = this.experience.debug
        this.renderer = this.experience.renderer

        // Setup
        this.colTexResource = this.resources.items.butterflyTex

        // Debug
        if (this.debug.active) {
            this.debugFolder = this.debug.ui.addFolder('Butterfly')
        }

        // update() variables
        this.butterflyPrevPos = new THREE.Vector3()
        this.butterflyTarget = new THREE.Vector3()
        /** lookAt() expects world space; built in xrRig-local then transformed. */
        this._butterflyLookAtWorld = new THREE.Vector3()
        this.butterflyVelocity = new THREE.Vector3()
        this.butterflyHasPrev = false
        this.butterflyForwardTilt = THREE.MathUtils.degToRad(20)

        this.setTextures()
        this.setMaterial()
        this.setModel()
    }

    setTextures() {
        this.textures = {}

        this.textures.color = this.colTexResource
        this.textures.color.colorSpace = THREE.SRGBColorSpace
    }

    setMaterial() {
        this.wingMatL = new THREE.MeshBasicMaterial({
            color: "#ffffff",
            map: this.textures.color,
            alphaTest: 0.5,
            side: THREE.DoubleSide
        })
        this.wingMatR = this.wingMatL.clone()

        const setupWingMaterial = (material) => {
            material.onBeforeCompile = (shader) => {
                // console.log(shader.fragmentShader)

                // uniforms available in vertex shader
                shader.uniforms.uTime = {value: 0};
                shader.uniforms.uWingSpeed = {value: 23.0};
                shader.uniforms.uFlapAmplitude = {value: 1.1}; // radians
                shader.uniforms.uHinge = {value: new THREE.Vector3(0, 0, 0)}; // object-space
                shader.uniforms.uAxis = {value: new THREE.Vector3(0, 1, 0)};  // rotation axis

                material.userData.shader = shader

                shader.vertexShader = `
                uniform float uTime;
                uniform float uWingSpeed;
                uniform float uFlapAmplitude;
                uniform vec3 uHinge;
                uniform vec3 uAxis;
            
                vec3 rotateAroundAxis(vec3 p, vec3 axis, float angle) {
                    axis = normalize(axis);
                    float s = sin(angle);
                    float c = cos(angle);
                    return p * c
                       + cross(axis, p) * s
                       + axis * dot(axis, p) * (1.0 - c);
                }
                ` + shader.vertexShader

                // 2) inject deformation logic inside main()
                shader.vertexShader = shader.vertexShader.replace(
                    '#include <begin_vertex>',
                    `
                    #include <begin_vertex>
                    
                    float flap = sin(uTime * uWingSpeed);
                    float angle = flap * uFlapAmplitude;
                    
                    // move vertex to hinge space
                    vec3 localPos = transformed - uHinge;
                    
                    // rotate around hinge axis
                    localPos = rotateAroundAxis(localPos, uAxis, angle);
                    
                    // move back
                    transformed = localPos + uHinge;    
                    `
                )

                // Keep reference if you want to update uniforms later
                material.userData.shader = shader

                shader.fragmentShader = shader.fragmentShader.replace(
                    '#include <map_fragment>',
                    `
                    #include <map_fragment>
                    
                    #ifdef USE_MAP
                        float ux = fract(vMapUv.x); // <- important: keep in 0..1 even with repeat/atlas transforms
                        if (ux < 0.5) discard; // discards U left side
                    #endif
                `
                )
            }
        }
        setupWingMaterial(this.wingMatL)
        setupWingMaterial(this.wingMatR)

        if (this.debug.active) {
            this.debugFolder.addColor(this.wingMatL, 'color').name('Wing Color').listen()
                .onChange(() => {
                    this.wingMatL.color.set(this.wingMatL.color)
                    this.wingMatR.color.set(this.wingMatL.color)
                })
        }
    }

    setModel() {
        // Butterfly body
        this.butterflyBody = new THREE.Mesh(
            new THREE.CapsuleGeometry(.02,.4,3,6),
            new THREE.MeshBasicMaterial({ color: 0x555555 }),
        )
        this.butterflyBody.scale.setScalar(.18)
        this.experience.xrRig.add(this.butterflyBody)

        // Wings
        const wingGeometry = new THREE.PlaneGeometry(1, 1, 1, 1)
        wingGeometry.rotateY(Math.PI / 8) // Give slight init Y angle
        const wingL = new THREE.Mesh(wingGeometry, this.wingMatL)
        const wingR = new THREE.Mesh(wingGeometry, this.wingMatR)
        wingL.position.set(0, 0.1, 0) // Slightly slides wings on the body
        wingR.position.set(0, 0.1, 0)
        wingR.scale.x = -1
        this.butterflyBody.add(wingL, wingR)
    }

    update() {
        // Butterfly wing animation
        if (this.wingMatL.userData.shader) {
            this.wingMatL.userData.shader.uniforms.uTime.value = this.time.elapsed * 0.001
        }
        if (this.wingMatR.userData.shader) {
            this.wingMatR.userData.shader.uniforms.uTime.value = this.time.elapsed * 0.001
        }

        // Butterfly
        const butterflyAngle = - this.time.elapsed * 0.001 * 0.5
        this.butterflyBody.position.x = Math.cos(butterflyAngle) * 1.33
        this.butterflyBody.position.z = Math.sin(butterflyAngle) * 1.33
        // this.butterflyBody.position.y = (Math.abs(Math.sin(butterflyAngle * 2.5)) * Math.abs(Math.sin(butterflyAngle * 1.2))) * 0.4 + 0.5
        const wave = Math.sin(butterflyAngle * 2)
        this.butterflyBody.position.y = Math.sign(wave) * Math.pow(Math.abs(wave), 3) * .25 + 0.666

        if (!this.butterflyHasPrev) {
            this.butterflyPrevPos.copy(this.butterflyBody.position)
            this.butterflyHasPrev = true
        }

        this.butterflyVelocity.subVectors(this.butterflyBody.position, this.butterflyPrevPos)
        const butterflyHorizLenSq = (this.butterflyVelocity.x * this.butterflyVelocity.x) + (this.butterflyVelocity.z * this.butterflyVelocity.z)
        if (butterflyHorizLenSq > 1e-6) {
            this.butterflyTarget.set(
                this.butterflyBody.position.x + this.butterflyVelocity.x,
                this.butterflyBody.position.y,
                this.butterflyBody.position.z + this.butterflyVelocity.z
            )
            this._butterflyLookAtWorld.copy(this.butterflyTarget)
            this.experience.xrRig.localToWorld(this._butterflyLookAtWorld)
            this.butterflyBody.lookAt(this._butterflyLookAtWorld)
            this.butterflyBody.rotateX(this.butterflyForwardTilt)
        }
        this.butterflyPrevPos.copy(this.butterflyBody.position)
    }
}

